import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout, { PageKey } from '../components/Layout';
import {
  getAdAccounts, getMarke, saveMarke,
  uploadLogo, deleteLogo, getFotos, uploadFotos, deleteFoto,
  getMasterSets, uploadVariation, replaceVariationImage,
  uploadMasterImage, getSettings, getHiggsfieldModels, API_BASE,
} from '../api';

interface Props {
  user: { email: string; name: string };
  onLogout: () => void;
  onNavigate: (page: PageKey) => void;
}

interface Account { id: string; name: string; }
interface Brand { primary: string; secondary: string; accent: string; logo_url?: string | null; }
interface Foto { filename: string; url: string; }
interface MasterSet {
  id: string; name: string; beschreibung: string; kategorie: string;
  images: string[]; image_count: number; farben?: string[];
}

type Tab = 'kunden' | 'master' | 'erstellen';
type Category = 'alle' | 'soka' | 'fitnessselbsttest' | 'hormontest';

export default function Creatives({ user, onLogout, onNavigate }: Props) {
  const [tab, setTab] = useState<Tab>('kunden');

  return (
    <Layout user={user} onLogout={onLogout} activePage="creatives" onNavigate={onNavigate}>
      <div style={s.page}>
        <div style={s.header}>
          <h1 style={s.title}>Creatives</h1>
          <p style={s.subtitle}>Marken-Assets verwalten, Master Creatives anpassen, KI-Bilder erstellen</p>
        </div>

        {/* Tab Bar */}
        <div style={s.tabBar}>
          <button onClick={() => setTab('kunden')} style={tab === 'kunden' ? s.tabActive : s.tab}>
            <ImageIcon size={14} />
            Kunden-Assets
          </button>
          <button onClick={() => setTab('master')} style={tab === 'master' ? s.tabActive : s.tab}>
            <LayoutIcon size={14} />
            Master Creatives
          </button>
          <button onClick={() => setTab('erstellen')} style={tab === 'erstellen' ? s.tabActive : s.tab}>
            <SparkIcon size={14} />
            Creative erstellen
            <span style={s.comingSoonBadge}>Higgsfield</span>
          </button>
        </div>

        {tab === 'kunden' && <KundenAssets />}
        {tab === 'master' && <MasterCreatives />}
        {tab === 'erstellen' && <CreativeErstellen />}
      </div>
    </Layout>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Kunden-Assets
// ══════════════════════════════════════════════════════════════════════════════

function KundenAssets() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [brand, setBrand] = useState<Brand>({ primary: '#1a3a5c', secondary: '#e8f0fe', accent: '#f59e0b' });
  const [fotos, setFotos] = useState<Foto[]>([]);
  const [loadingBrand, setLoadingBrand] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [savedBrand, setSavedBrand] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFotos, setUploadingFotos] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const fotosRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getAdAccounts()
      .then(res => {
        const accs: Account[] = (res.data.accounts || []).map((a: any) => ({ id: a.id, name: a.name }));
        setAccounts(accs);
        if (accs.length > 0) setSelectedId(accs[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoadingBrand(true);
    Promise.all([getMarke(selectedId), getFotos(selectedId)])
      .then(([mRes, fRes]) => {
        setBrand(mRes.data);
        setFotos(fRes.data.fotos || []);
      })
      .catch(() => {})
      .finally(() => setLoadingBrand(false));
  }, [selectedId]);

  const handleSaveBrand = async () => {
    if (!selectedId) return;
    setSavingBrand(true);
    try {
      await saveMarke(selectedId, { primary: brand.primary, secondary: brand.secondary, accent: brand.accent });
      setSavedBrand(true);
      setTimeout(() => setSavedBrand(false), 2000);
    } finally {
      setSavingBrand(false);
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!selectedId) return;
    setUploadingLogo(true);
    try {
      const res = await uploadLogo(selectedId, file);
      setBrand(b => ({ ...b, logo_url: res.data.url }));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!selectedId) return;
    await deleteLogo(selectedId);
    setBrand(b => ({ ...b, logo_url: null }));
  };

  const handleFotosUpload = useCallback(async (files: File[]) => {
    if (!selectedId || files.length === 0) return;
    setUploadingFotos(true);
    try {
      const res = await uploadFotos(selectedId, files);
      setFotos(f => [...f, ...(res.data.uploaded || [])]);
    } finally {
      setUploadingFotos(false);
    }
  }, [selectedId]);

  const handleDeleteFoto = async (filename: string) => {
    if (!selectedId) return;
    await deleteFoto(selectedId, filename);
    setFotos(f => f.filter(x => x.filename !== filename));
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    handleFotosUpload(files);
  }, [handleFotosUpload]);

  const selectedAccount = accounts.find(a => a.id === selectedId);

  if (accounts.length === 0) {
    return (
      <div style={s.emptyState}>
        <ImageIcon size={32} color="#aeaeb2" />
        <div style={{ color: '#6e6e73', marginTop: 12 }}>Keine Partner-Accounts gefunden.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={s.partnerRow}>
        <label style={s.partnerLabel}>Partner</label>
        <select value={selectedId} onChange={e => setSelectedId(e.target.value)} style={s.select}>
          {accounts.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      {loadingBrand ? (
        <div style={s.loadingBox}>Lade Assets…</div>
      ) : (
        <div style={s.twoCol}>
          {/* LEFT – Marken-Identität */}
          <div style={s.card}>
            <div style={s.cardTitle}>
              <BrushIcon size={15} />
              Marken-Identität
            </div>

            <div style={s.colorSection}>
              {[
                { key: 'primary', label: 'Primärfarbe' },
                { key: 'secondary', label: 'Sekundärfarbe' },
                { key: 'accent', label: 'Akzentfarbe' },
              ].map(({ key, label }) => (
                <div key={key} style={s.colorRow}>
                  <div style={s.colorLabel}>{label}</div>
                  <div style={s.colorPickerWrap}>
                    <input
                      type="color"
                      value={(brand as any)[key]}
                      onChange={e => setBrand(b => ({ ...b, [key]: e.target.value }))}
                      style={s.colorInput}
                    />
                    <div style={{ ...s.colorSwatch, background: (brand as any)[key] }} />
                    <span style={s.colorHex}>{((brand as any)[key] as string).toUpperCase()}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={s.colorPreview}>
              <div style={{ ...s.previewChip, background: brand.primary, color: '#fff' }}>Primär</div>
              <div style={{ ...s.previewChip, background: brand.secondary, color: '#1d1d1f', border: '1px solid #e8e8ed' }}>Sekundär</div>
              <div style={{ ...s.previewChip, background: brand.accent, color: '#fff' }}>Akzent</div>
            </div>

            <div style={s.logoSection}>
              <div style={s.fieldLabel}>Logo</div>
              {brand.logo_url ? (
                <div style={s.logoPreviewWrap}>
                  <img
                    src={`${API_BASE}${brand.logo_url}`}
                    alt="Logo"
                    style={s.logoPreview}
                  />
                  <div style={s.logoActions}>
                    <button onClick={() => logoRef.current?.click()} style={s.btnSmall} disabled={uploadingLogo}>
                      Ersetzen
                    </button>
                    <button onClick={handleDeleteLogo} style={s.btnSmallDanger}>
                      Löschen
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => logoRef.current?.click()}
                  disabled={uploadingLogo}
                  style={s.uploadLogoBtn}
                >
                  <UploadIcon size={16} />
                  {uploadingLogo ? 'Hochladen…' : 'Logo hochladen'}
                </button>
              )}
              <input
                ref={logoRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); e.target.value = ''; }}
              />
            </div>

            {selectedAccount && (
              <div style={s.locationRow}>
                <div style={s.fieldLabel}>Standort</div>
                <div style={s.locationValue}>{selectedAccount.name}</div>
              </div>
            )}

            <button
              onClick={handleSaveBrand}
              disabled={savingBrand}
              style={savedBrand ? s.btnSaved : s.btnPrimary}
            >
              {savingBrand ? 'Speichern…' : savedBrand ? '✓ Gespeichert' : 'Marke speichern'}
            </button>
          </div>

          {/* RIGHT – Medien-Galerie */}
          <div style={s.card}>
            <div style={s.cardTitle}>
              <GalleryIcon size={15} />
              Medien-Galerie
              <span style={s.countBadge}>{fotos.length}</span>
            </div>

            <div
              style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}) }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fotosRef.current?.click()}
            >
              <UploadIcon size={20} color={dragOver ? '#1a3a5c' : '#aeaeb2'} />
              <div style={s.dropText}>
                {uploadingFotos ? 'Hochladen…' : 'Bilder hier ablegen oder klicken'}
              </div>
              <div style={s.dropHint}>PNG, JPG, WebP – mehrere Dateien möglich</div>
            </div>
            <input
              ref={fotosRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={e => {
                const files = Array.from(e.target.files || []);
                handleFotosUpload(files);
                e.target.value = '';
              }}
            />

            {fotos.length > 0 && (
              <div style={s.photoGrid}>
                {fotos.map(foto => (
                  <div key={foto.filename} style={s.photoItem}>
                    <img src={`${API_BASE}${foto.url}`} alt={foto.filename} style={s.photoImg} />
                    <button onClick={() => handleDeleteFoto(foto.filename)} style={s.photoDelete} title="Löschen">×</button>
                  </div>
                ))}
              </div>
            )}

            {fotos.length === 0 && !uploadingFotos && (
              <div style={s.emptyGallery}>Noch keine Bilder hochgeladen</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Master Creatives
// ══════════════════════════════════════════════════════════════════════════════

function MasterCreatives() {
  const [sets, setSets] = useState<MasterSet[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<Category>('alle');
  const [lightbox, setLightbox] = useState<{ set: MasterSet; idx: number } | null>(null);
  const [variationModal, setVariationModal] = useState<MasterSet | null>(null);
  const [masterUploadTarget, setMasterUploadTarget] = useState<{ set: MasterSet; idx: number } | null>(null);
  const masterUploadRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    Promise.all([getMasterSets(), getAdAccounts()])
      .then(([sRes, aRes]) => {
        setSets(sRes.data.sets || []);
        setAccounts((aRes.data.accounts || []).map((a: any) => ({ id: a.id, name: a.name })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = category === 'alle' ? sets : sets.filter(set => set.kategorie === category);

  const categories: { key: Category; label: string }[] = [
    { key: 'alle', label: 'Alle' },
    { key: 'soka', label: 'SOKA' },
    { key: 'fitnessselbsttest', label: 'Fitnessselbsttest' },
    { key: 'hormontest', label: 'Hormontest' },
  ];

  const handleMasterImageUpload = async (file: File) => {
    if (!masterUploadTarget) return;
    try {
      const res = await uploadMasterImage(masterUploadTarget.set.id, masterUploadTarget.idx + 1, file);
      const newUrl = res.data.url;
      setSets(prev => prev.map(set => {
        if (set.id !== masterUploadTarget.set.id) return set;
        const imgs = [...set.images];
        imgs[masterUploadTarget.idx] = newUrl;
        return { ...set, images: imgs, image_count: Math.max(set.image_count, masterUploadTarget.idx + 1) };
      }));
    } catch {}
    setMasterUploadTarget(null);
  };

  const prevLight = () => setLightbox(l => l ? { ...l, idx: (l.idx - 1 + l.set.images.length) % l.set.images.length } : null);
  const nextLight = () => setLightbox(l => l ? { ...l, idx: (l.idx + 1) % l.set.images.length } : null);

  if (loading) return <div style={s.loadingBox}>Lade Master Creatives…</div>;

  return (
    <div>
      <div style={s.filterRow}>
        {categories.map(c => (
          <button key={c.key} onClick={() => setCategory(c.key)} style={category === c.key ? s.filterActive : s.filterBtn}>
            {c.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={s.emptyState}>
          <LayoutIcon size={32} color="#aeaeb2" />
          <div style={{ color: '#6e6e73', marginTop: 12, textAlign: 'center' as const }}>
            Keine Master Creatives gefunden.<br />
            <span style={{ fontSize: 12 }}>Lege Bilder in <code>backend/assets/master/</code> ab.</span>
          </div>
        </div>
      )}

      <div style={s.masterGrid}>
        {filtered.map(set => (
          <div key={set.id} style={s.masterCard}>
            <div style={s.masterCardHeader}>
              <div>
                <div style={s.masterCardTitle}>{set.name}</div>
                <div style={s.masterCardDesc}>{set.beschreibung}</div>
              </div>
              <span style={{ ...s.categoryPill, ...categoryColor(set.kategorie) }}>{set.kategorie}</span>
            </div>

            <div style={s.imageGrid23}>
              {Array.from({ length: 6 }).map((_, i) => {
                const imgUrl = set.images[i];
                return (
                  <div key={i} style={s.imageSlot}>
                    {imgUrl ? (
                      <img
                        src={`${API_BASE}${imgUrl}`}
                        alt={`Bild ${i + 1}`}
                        style={s.imageSlotImg}
                        onClick={() => setLightbox({ set, idx: i })}
                      />
                    ) : (
                      <div
                        style={{ ...s.imagePlaceholder, background: placeholderGradient(set.farben || [], i) }}
                        onClick={() => { setMasterUploadTarget({ set, idx: i }); masterUploadRef.current?.click(); }}
                      >
                        <div style={s.placeholderLabel}>Bild {i + 1}</div>
                        <div style={s.placeholderUploadHint}><UploadIcon size={11} color="rgba(255,255,255,.7)" /></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={s.masterCardFooter}>
              <span style={s.imageCountHint}>{set.image_count}/6 Bilder</span>
              <button onClick={() => setVariationModal(set)} style={s.btnPrimary}>
                Variation erstellen →
              </button>
            </div>
          </div>
        ))}
      </div>

      <input
        ref={masterUploadRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleMasterImageUpload(f); e.target.value = ''; }}
      />

      {lightbox && (
        <div style={s.lightboxOverlay} onClick={() => setLightbox(null)}>
          <div style={s.lightboxContent} onClick={e => e.stopPropagation()}>
            <button style={s.lightboxClose} onClick={() => setLightbox(null)}>×</button>
            <button style={s.lightboxArrow} onClick={prevLight}>‹</button>
            <img src={`${API_BASE}${lightbox.set.images[lightbox.idx]}`} alt="" style={s.lightboxImg} />
            <button style={s.lightboxArrow} onClick={nextLight}>›</button>
            <div style={s.lightboxCaption}>
              {lightbox.set.name} – Bild {lightbox.idx + 1}/{lightbox.set.images.length}
            </div>
          </div>
        </div>
      )}

      {variationModal && (
        <VariationModal
          set={variationModal}
          accounts={accounts}
          onClose={() => setVariationModal(null)}
        />
      )}
    </div>
  );
}

function VariationModal({ set, accounts, onClose }: { set: MasterSet; accounts: Account[]; onClose: () => void; }) {
  const [selectedId, setSelectedId] = useState(accounts[0]?.id || '');
  const [varImages, setVarImages] = useState<(string | null)[]>(Array(6).fill(null));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [view, setView] = useState<'master' | 'variation'>('master');
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const slotRef = useRef<HTMLInputElement>(null);

  const handleSlotUpload = async (file: File) => {
    if (activeSlot === null) return;
    setUploading(true);
    try {
      const res = await replaceVariationImage(selectedId, set.id, activeSlot + 1, file);
      setVarImages(imgs => { const n = [...imgs]; n[activeSlot] = res.data.url; return n; });
    } finally {
      setUploading(false);
      setActiveSlot(null);
    }
  };

  const handleBulkUpload = async (files: File[]) => {
    setUploading(true);
    try {
      const res = await uploadVariation(selectedId, set.id, files);
      const uploaded: string[] = (res.data.uploaded || []).map((u: any) => u.url);
      setVarImages(imgs => { const n = [...imgs]; uploaded.forEach((url, i) => { n[i] = url; }); return n; });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 400));
    setSaved(true);
    setSaving(false);
    setTimeout(onClose, 1400);
  };

  const allReady = varImages.filter(Boolean).length === 6;

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>Variation erstellen</div>
            <div style={s.modalSubtitle}>{set.name}</div>
          </div>
          <button onClick={onClose} style={s.modalClose}>×</button>
        </div>

        <div style={s.modalControls}>
          <div style={{ flex: 1 }}>
            <div style={{ ...s.fieldLabel, marginBottom: 6 }}>Partner</div>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)} style={s.select}>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div style={s.viewToggle}>
            <button onClick={() => setView('master')} style={view === 'master' ? s.viewBtnActive : s.viewBtn}>Master</button>
            <button onClick={() => setView('variation')} style={view === 'variation' ? s.viewBtnActive : s.viewBtn}>Partner</button>
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <div style={s.imageGrid23}>
            {Array.from({ length: 6 }).map((_, i) => {
              const masterImg = set.images[i];
              const varImg = varImages[i];
              const displayUrl = view === 'variation' ? varImg : masterImg;
              return (
                <div key={i} style={s.imageSlot}>
                  {displayUrl ? (
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      <img src={`${API_BASE}${displayUrl}`} alt="" style={s.imageSlotImg} />
                      {view === 'variation' && (
                        <button
                          style={s.slotReplaceBtn}
                          onClick={() => { setActiveSlot(i); slotRef.current?.click(); }}
                          title="Ersetzen"
                        >
                          <UploadIcon size={10} color="#fff" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{ ...s.imagePlaceholder, background: placeholderGradient(set.farben || [], i), cursor: view === 'variation' ? 'pointer' : 'default' }}
                      onClick={() => { if (view === 'variation') { setActiveSlot(i); slotRef.current?.click(); } }}
                    >
                      <div style={s.placeholderLabel}>Bild {i + 1}</div>
                      {view === 'variation' && <UploadIcon size={14} color="rgba(255,255,255,.7)" />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div style={s.modalFooter}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' as const }}>
            <button
              style={s.btnOutline}
              disabled={uploading}
              onClick={() => {
                const inp = document.createElement('input');
                inp.type = 'file'; inp.multiple = true; inp.accept = 'image/*';
                inp.onchange = e => {
                  const files = Array.from((e.target as HTMLInputElement).files || []);
                  if (files.length) handleBulkUpload(files);
                };
                inp.click();
              }}
            >
              <UploadIcon size={13} />
              {uploading ? 'Hochladen…' : 'Alle 6 hochladen'}
            </button>
            <button style={{ ...s.btnOutline, opacity: 0.45, cursor: 'not-allowed' }} disabled>
              <SparkIcon size={13} />
              Higgsfield (bald)
            </button>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !allReady}
            style={{ ...(saved ? s.btnSaved : s.btnPrimary), opacity: (!allReady && !saved) ? 0.5 : 1 }}
            title={!allReady ? 'Alle 6 Bilder hochladen' : undefined}
          >
            {saving ? 'Speichern…' : saved ? '✓ Gespeichert' : `In Kampagne einfügen (${varImages.filter(Boolean).length}/6)`}
          </button>
        </div>

        <input
          ref={slotRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleSlotUpload(f); e.target.value = ''; }}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — Creative erstellen (Higgsfield)
// ══════════════════════════════════════════════════════════════════════════════

interface HiggsfieldModel {
  id: string; name: string; description: string;
  supports_text: boolean; supports_image: boolean;
}

const LS_MODEL_KEY = 'ps_higgsfield_last_model';

function CreativeErstellen() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [higgsfieldSet, setHiggsfieldSet] = useState(false);

  // Mode toggle
  const [mode, setMode] = useState<'prompt' | 'bild'>('prompt');

  // Prompt-mode fields
  const [prompt, setPrompt] = useState('');

  // Bild-mode fields
  const [masterAdFile, setMasterAdFile] = useState<File | null>(null);
  const [masterAdPreview, setMasterAdPreview] = useState<string | null>(null);
  const [bildPrompt, setBildPrompt] = useState('');
  const [bildDragOver, setBildDragOver] = useState(false);
  const bildRef = useRef<HTMLInputElement>(null);

  // Shared fields
  const [style, setStyle] = useState<'realistisch' | 'grafisch' | 'mixed'>('realistisch');
  const [formats, setFormats] = useState<Set<string>>(new Set(['4:5']));

  // Model selection
  const [models, setModels] = useState<HiggsfieldModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>(
    () => localStorage.getItem(LS_MODEL_KEY) || ''
  );
  const [modelsSource, setModelsSource] = useState<'default' | 'api'>('default');

  useEffect(() => {
    getAdAccounts()
      .then(res => {
        const accs: Account[] = (res.data.accounts || []).map((a: any) => ({ id: a.id, name: a.name }));
        setAccounts(accs);
        if (accs.length > 0) setSelectedId(accs[0].id);
      })
      .catch(() => {});
    getSettings()
      .then(res => setHiggsfieldSet(!!res.data.HIGGSFIELD_SET))
      .catch(() => {});
    getHiggsfieldModels()
      .then(res => {
        const list: HiggsfieldModel[] = res.data.models || [];
        setModels(list);
        setModelsSource(res.data.source || 'default');
        if (!localStorage.getItem(LS_MODEL_KEY) && list.length > 0) {
          setSelectedModel(list[0].id);
        }
      })
      .catch(() => {});
  }, []);

  const handleModelChange = (id: string) => {
    setSelectedModel(id);
    localStorage.setItem(LS_MODEL_KEY, id);
  };

  const toggleFormat = (f: string) => {
    setFormats(prev => {
      const n = new Set(prev);
      if (n.has(f)) { if (n.size > 1) n.delete(f); } else n.add(f);
      return n;
    });
  };

  const handleMasterAdSelect = (file: File) => {
    setMasterAdFile(file);
    const reader = new FileReader();
    reader.onload = e => setMasterAdPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleBildDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setBildDragOver(false);
    const file = Array.from(e.dataTransfer.files).find(f => f.type.startsWith('image/'));
    if (file) handleMasterAdSelect(file);
  };

  // Filter models by mode capability
  const availableModels = models.filter(m =>
    mode === 'prompt' ? m.supports_text : m.supports_image
  );

  // Auto-switch model if current one doesn't support new mode
  useEffect(() => {
    if (!availableModels.find(m => m.id === selectedModel) && availableModels.length > 0) {
      handleModelChange(availableModels[0].id);
    }
  }, [mode, models]);

  const currentModel = models.find(m => m.id === selectedModel);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {!higgsfieldSet && (
        <div style={s.warningBox}>
          <InfoIcon size={14} color="#92400e" />
          <span>
            Kein Higgsfield API Key konfiguriert.{' '}
            Unter <strong>Einstellungen</strong> den Key hinterlegen.
          </span>
        </div>
      )}

      {/* Mode Toggle */}
      <div style={s.modeToggleRow}>
        <div style={s.modeToggle}>
          <button
            onClick={() => setMode('prompt')}
            style={mode === 'prompt' ? s.modeTabActive : s.modeTab}
          >
            <SparkIcon size={13} />
            Aus Prompt
          </button>
          <button
            onClick={() => setMode('bild')}
            style={mode === 'bild' ? s.modeTabActive : s.modeTab}
          >
            <ImageIcon size={13} />
            Aus Bild
          </button>
        </div>
        <div style={s.modeDesc}>
          {mode === 'prompt'
            ? 'Text-Beschreibung → 6 Variationen'
            : 'Master-Ad hochladen → Higgsfield variiert'}
        </div>
      </div>

      <div style={s.erstellenGrid}>
        {/* LEFT – Konfiguration */}
        <div style={s.card}>
          <div style={s.cardTitle}>
            <SparkIcon size={15} />
            Generierungs-Einstellungen
          </div>

          {/* Partner */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={s.fieldLabel}>Partner (Farben & Logo werden übergeben)</label>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)} style={s.select}>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          {/* Mode-specific inputs */}
          {mode === 'prompt' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={s.fieldLabel}>Prompt</label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="z.B. 'Motiviertes Fitnessstudio-Mitglied beim Training, dynamisch, modern, Partnerfarben verwenden'"
                style={s.promptTextarea}
                rows={4}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ ...s.fieldLabel, marginBottom: 8, display: 'block' }}>Master-Ad (Original-Bild)</label>
                {masterAdPreview ? (
                  <div style={s.masterAdPreviewWrap}>
                    <img src={masterAdPreview} alt="Master Ad" style={s.masterAdPreviewImg} />
                    <div style={s.masterAdOverlay}>
                      <button
                        style={s.masterAdReplaceBtn}
                        onClick={() => bildRef.current?.click()}
                      >
                        <UploadIcon size={12} color="#fff" />
                        Ersetzen
                      </button>
                    </div>
                    <div style={s.masterAdFilename}>{masterAdFile?.name}</div>
                  </div>
                ) : (
                  <div
                    style={{ ...s.dropZone, ...(bildDragOver ? s.dropZoneActive : {}), minHeight: 120 }}
                    onDragOver={e => { e.preventDefault(); setBildDragOver(true); }}
                    onDragLeave={() => setBildDragOver(false)}
                    onDrop={handleBildDrop}
                    onClick={() => bildRef.current?.click()}
                  >
                    <ImageIcon size={22} color={bildDragOver ? '#1a3a5c' : '#aeaeb2'} />
                    <div style={s.dropText}>Master-Ad hier ablegen oder klicken</div>
                    <div style={s.dropHint}>PNG, JPG, WebP</div>
                  </div>
                )}
                <input
                  ref={bildRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleMasterAdSelect(f); e.target.value = ''; }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={s.fieldLabel}>
                  Anpassungs-Prompt <span style={{ color: '#aeaeb2', fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea
                  value={bildPrompt}
                  onChange={e => setBildPrompt(e.target.value)}
                  placeholder="z.B. 'Passe an mit Partnerfarben und Logo, Fitness-Atmosphäre beibehalten'"
                  style={{ ...s.promptTextarea, minHeight: 72 }}
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Model Dropdown */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={s.fieldLabel}>Modell</label>
              {modelsSource === 'api' && <span style={s.apiSourceBadge}>Live von API</span>}
              {modelsSource === 'default' && <span style={s.defaultSourceBadge}>Standard-Liste</span>}
            </div>
            <select
              value={selectedModel}
              onChange={e => handleModelChange(e.target.value)}
              style={s.select}
            >
              {availableModels.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
            {currentModel && (
              <div style={s.modelDesc}>{currentModel.description}</div>
            )}
          </div>

          {/* Style */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={s.fieldLabel}>Stil</div>
            <div style={s.styleButtons}>
              {(['realistisch', 'grafisch', 'mixed'] as const).map(st => (
                <button key={st} onClick={() => setStyle(st)} style={style === st ? s.filterActive : s.filterBtn}>
                  {st === 'realistisch' ? 'Foto-realistisch' : st === 'grafisch' ? 'Grafisch' : 'Mixed'}
                </button>
              ))}
            </div>
          </div>

          {/* Format */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={s.fieldLabel}>Format</div>
            <div style={s.styleButtons}>
              {['4:5', '9:16'].map(fmt => (
                <button key={fmt} onClick={() => toggleFormat(fmt)} style={formats.has(fmt) ? s.filterActive : s.filterBtn}>
                  {fmt === '4:5' ? '4:5 (Feed)' : '9:16 (Stories)'}
                </button>
              ))}
            </div>
          </div>

          <div style={s.erstellenHint}>
            Generiert immer <strong>6 Variationen</strong>. Markenfarben und Logo des gewählten Partners
            {mode === 'bild' ? ' sowie das hochgeladene Bild werden' : ' werden'} automatisch an Higgsfield übergeben.
          </div>

          <button
            disabled
            style={{ ...s.btnPrimary, opacity: 0.45, cursor: 'not-allowed', width: '100%', justifyContent: 'center' }}
          >
            <SparkIcon size={14} />
            Generieren – kommt bald
          </button>
        </div>

        {/* RIGHT – Preview */}
        <div style={s.card}>
          <div style={s.cardTitle}>
            <GalleryIcon size={15} />
            Generierte Creatives
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#aeaeb2', fontWeight: 400 }}>6 Variationen</span>
          </div>

          <div style={s.previewGrid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={s.previewSlot}>
                {mode === 'bild' && masterAdPreview ? (
                  <>
                    <img src={masterAdPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.25, position: 'absolute', inset: 0 } as React.CSSProperties} />
                    <SparkIcon size={18} color="#818cf8" />
                    <div style={{ fontSize: 10, color: '#a5b4fc', marginTop: 5, position: 'relative' }}>Variation {i + 1}</div>
                  </>
                ) : (
                  <>
                    <SparkIcon size={20} color="#c7d2fe" />
                    <div style={{ fontSize: 11, color: '#a5b4fc', marginTop: 6 }}>Variation {i + 1}</div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
            <button disabled style={{ ...s.btnOutline, opacity: 0.4, cursor: 'not-allowed' }}>Als Master speichern</button>
            <button disabled style={{ ...s.btnOutline, opacity: 0.4, cursor: 'not-allowed' }}>Für Partner speichern</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Icons
// ══════════════════════════════════════════════════════════════════════════════

interface IconProps { size?: number; color?: string; }

function ImageIcon({ size = 16, color = '#6e6e73' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
}
function LayoutIcon({ size = 16, color = '#6e6e73' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
}
function SparkIcon({ size = 16, color = 'currentColor' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
}
function UploadIcon({ size = 16, color = 'currentColor' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>;
}
function BrushIcon({ size = 16, color = '#6e6e73' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08"/><path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1 1.08 2.26 2.02 3.98 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-2.98-3.02z"/></svg>;
}
function GalleryIcon({ size = 16, color = '#6e6e73' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
}
function InfoIcon({ size = 16, color = '#92400e' }: IconProps) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
}

// ══════════════════════════════════════════════════════════════════════════════
// Helpers
// ══════════════════════════════════════════════════════════════════════════════

function categoryColor(kat: string): React.CSSProperties {
  const map: Record<string, React.CSSProperties> = {
    soka: { background: '#dbeafe', color: '#1e40af' },
    fitnessselbsttest: { background: '#d1fae5', color: '#065f46' },
    hormontest: { background: '#ede9fe', color: '#5b21b6' },
  };
  return map[kat] || { background: '#f0f0f5', color: '#6e6e73' };
}

function placeholderGradient(farben: string[], idx: number): string {
  const defaults = ['#1a3a5c', '#2d6a9f', '#0f2840'];
  const base = (farben.length > 0 ? farben[idx % farben.length] : defaults[idx % defaults.length]);
  return `linear-gradient(135deg, ${base}dd, ${base}88)`;
}

// ══════════════════════════════════════════════════════════════════════════════
// Styles
// ══════════════════════════════════════════════════════════════════════════════

const s: Record<string, React.CSSProperties> = {
  page: { padding: '40px 48px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 32 },
  title: { fontSize: 28, fontWeight: 800, color: '#1d1d1f', margin: 0, letterSpacing: '-0.5px' },
  subtitle: { margin: '6px 0 0', color: '#6e6e73', fontSize: 14 },

  tabBar: { display: 'flex', gap: 4, marginBottom: 32, background: '#f0f0f5', borderRadius: 12, padding: 4 },
  tab: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: 'transparent', border: 'none', borderRadius: 9, padding: '9px 18px',
    fontSize: 13.5, fontWeight: 500, color: '#6e6e73', cursor: 'pointer',
  },
  tabActive: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: '#ffffff', border: 'none', borderRadius: 9, padding: '9px 18px',
    fontSize: 13.5, fontWeight: 600, color: '#1a3a5c', cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  },
  comingSoonBadge: {
    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    color: '#fff', fontSize: 9.5, fontWeight: 700,
    borderRadius: 5, padding: '2px 7px', marginLeft: 4,
  },

  partnerRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 },
  partnerLabel: { fontSize: 13, fontWeight: 600, color: '#6e6e73', minWidth: 56 },
  select: {
    border: '1.5px solid #e8e8ed', borderRadius: 8, padding: '8px 12px',
    fontSize: 13, color: '#1d1d1f', background: '#fff', cursor: 'pointer', outline: 'none', minWidth: 220,
  },
  loadingBox: { color: '#6e6e73', fontSize: 13, padding: '40px 0', textAlign: 'center' as const },
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '80px 0', color: '#aeaeb2', fontSize: 14,
  },

  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20 },
  card: {
    background: '#fff', borderRadius: 14, border: '1px solid #e8e8ed',
    padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 18,
  },
  cardTitle: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 15, fontWeight: 700, color: '#1d1d1f',
  },
  countBadge: {
    marginLeft: 6, background: '#f0f0f5', color: '#6e6e73',
    fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '2px 8px',
  },

  colorSection: { display: 'flex', flexDirection: 'column', gap: 12 },
  colorRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  colorLabel: { fontSize: 13, color: '#3d3d41', fontWeight: 500 },
  colorPickerWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  colorInput: { width: 36, height: 28, border: 'none', borderRadius: 6, cursor: 'pointer', padding: 0 },
  colorSwatch: { width: 24, height: 24, borderRadius: 6, border: '1px solid rgba(0,0,0,.1)' },
  colorHex: { fontSize: 12, fontFamily: 'monospace', color: '#6e6e73', minWidth: 60 },
  colorPreview: { display: 'flex', gap: 8 },
  previewChip: {
    flex: 1, textAlign: 'center' as const, padding: '7px 0',
    borderRadius: 8, fontSize: 12, fontWeight: 600,
  },

  logoSection: { display: 'flex', flexDirection: 'column', gap: 8 },
  logoPreviewWrap: { display: 'flex', alignItems: 'center', gap: 14 },
  logoPreview: { width: 80, height: 80, objectFit: 'contain' as const, borderRadius: 10, border: '1px solid #e8e8ed', background: '#fafafa' },
  logoActions: { display: 'flex', flexDirection: 'column', gap: 6 },
  uploadLogoBtn: {
    display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
    border: '1.5px dashed #c8c8d0', borderRadius: 10, padding: '14px 16px',
    background: '#fafafa', color: '#6e6e73', fontSize: 13, cursor: 'pointer',
  },
  locationRow: { display: 'flex', flexDirection: 'column', gap: 4 },
  locationValue: { fontSize: 13, color: '#6e6e73' },
  fieldLabel: { fontSize: 12.5, fontWeight: 600, color: '#3d3d41' },

  btnPrimary: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 9,
    padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  btnSaved: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: '#10b981', color: '#fff', border: 'none', borderRadius: 9,
    padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'default',
  },
  btnOutline: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: 'transparent', color: '#1a3a5c', border: '1.5px solid rgba(26,58,92,.5)',
    borderRadius: 9, padding: '8px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
  },
  btnSmall: {
    background: '#f5f5f7', color: '#1d1d1f', border: '1px solid #e8e8ed',
    borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer',
  },
  btnSmallDanger: {
    background: '#fff2f2', color: '#cc2a22', border: '1px solid rgba(255,59,48,.2)',
    borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer',
  },

  dropZone: {
    border: '2px dashed #d1d1d6', borderRadius: 12, padding: '28px 20px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    cursor: 'pointer', background: '#fafafa',
  },
  dropZoneActive: { borderColor: '#1a3a5c', background: 'rgba(26,58,92,0.04)' },
  dropText: { fontSize: 13, color: '#6e6e73', fontWeight: 500 },
  dropHint: { fontSize: 11.5, color: '#aeaeb2' },

  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 },
  photoItem: { position: 'relative', borderRadius: 8, overflow: 'hidden', aspectRatio: '1' },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' as const, display: 'block' },
  photoDelete: {
    position: 'absolute', top: 4, right: 4,
    background: 'rgba(0,0,0,.55)', color: '#fff', border: 'none',
    borderRadius: '50%', width: 20, height: 20, fontSize: 14,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, lineHeight: '20px',
  },
  emptyGallery: { fontSize: 12.5, color: '#aeaeb2', textAlign: 'center' as const, padding: '20px 0' },

  filterRow: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' as const },
  filterBtn: {
    background: '#f5f5f7', color: '#6e6e73', border: 'none', borderRadius: 8,
    padding: '7px 16px', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
  },
  filterActive: {
    background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 8,
    padding: '7px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
  },

  masterGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 20 },
  masterCard: {
    background: '#fff', borderRadius: 14, border: '1px solid #e8e8ed',
    padding: '20px', display: 'flex', flexDirection: 'column', gap: 16,
  },
  masterCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  masterCardTitle: { fontSize: 15, fontWeight: 700, color: '#1d1d1f' },
  masterCardDesc: { fontSize: 12, color: '#6e6e73', marginTop: 3, lineHeight: 1.4 },
  categoryPill: { fontSize: 10.5, fontWeight: 700, borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' as const, flexShrink: 0 },

  imageGrid23: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: 6 },
  imageSlot: { borderRadius: 8, overflow: 'hidden', aspectRatio: '4/5', cursor: 'pointer' },
  imageSlotImg: { width: '100%', height: '100%', objectFit: 'cover' as const, display: 'block' },
  imagePlaceholder: {
    width: '100%', height: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
  },
  placeholderLabel: { fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.8)' },
  placeholderUploadHint: { opacity: 0.7 },

  masterCardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  imageCountHint: { fontSize: 12, color: '#aeaeb2' },

  lightboxOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  lightboxContent: {
    position: 'relative', maxWidth: '90vw', maxHeight: '90vh',
    display: 'flex', alignItems: 'center', gap: 16,
  },
  lightboxImg: { maxWidth: '80vw', maxHeight: '85vh', borderRadius: 12, objectFit: 'contain' as const },
  lightboxClose: {
    position: 'absolute', top: -44, right: 0,
    background: 'transparent', border: 'none', color: '#fff',
    fontSize: 34, cursor: 'pointer', lineHeight: 1, padding: 0,
  },
  lightboxArrow: {
    background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
    fontSize: 28, borderRadius: '50%', width: 44, height: 44,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  lightboxCaption: {
    position: 'absolute', bottom: -30, left: '50%',
    transform: 'translateX(-50%)', color: 'rgba(255,255,255,.6)',
    fontSize: 12, whiteSpace: 'nowrap' as const,
  },

  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20,
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680,
    maxHeight: '90vh', overflowY: 'auto' as const,
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,.25)',
  },
  modalHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '22px 24px 16px', borderBottom: '1px solid #f0f0f5',
  },
  modalTitle: { fontSize: 17, fontWeight: 700, color: '#1d1d1f' },
  modalSubtitle: { fontSize: 12.5, color: '#6e6e73', marginTop: 3 },
  modalClose: {
    background: '#f5f5f7', border: 'none', borderRadius: '50%',
    width: 30, height: 30, fontSize: 18, cursor: 'pointer', color: '#6e6e73',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  modalControls: {
    display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' as const,
    padding: '16px 24px', borderBottom: '1px solid #f0f0f5',
  },
  viewToggle: { display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #e8e8ed', flexShrink: 0 },
  viewBtn: {
    background: '#fff', border: 'none', padding: '7px 16px',
    fontSize: 12.5, fontWeight: 500, color: '#6e6e73', cursor: 'pointer',
  },
  viewBtnActive: {
    background: '#1a3a5c', border: 'none', padding: '7px 16px',
    fontSize: 12.5, fontWeight: 600, color: '#fff', cursor: 'pointer',
  },
  modalFooter: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '16px 24px', borderTop: '1px solid #f0f0f5', flexWrap: 'wrap' as const,
  },
  slotReplaceBtn: {
    position: 'absolute', bottom: 4, right: 4,
    background: 'rgba(0,0,0,.6)', border: 'none', borderRadius: 5,
    padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
  },

  erstellenGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  promptTextarea: {
    border: '1.5px solid #e8e8ed', borderRadius: 8, padding: '10px 12px',
    fontSize: 13, color: '#1d1d1f', outline: 'none', background: '#fafafa',
    resize: 'vertical' as const, fontFamily: 'inherit', lineHeight: 1.5,
  },
  styleButtons: { display: 'flex', gap: 8, flexWrap: 'wrap' as const },
  erstellenHint: {
    fontSize: 12, color: '#6e6e73', background: '#f5f5f7',
    borderRadius: 8, padding: '10px 12px', lineHeight: 1.5,
  },
  previewGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: 8 },
  previewSlot: {
    background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)',
    borderRadius: 10, aspectRatio: '4/5',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  },

  warningBox: {
    background: '#fffbeb', border: '1px solid rgba(245,158,11,.35)', borderRadius: 10,
    padding: '11px 16px', color: '#92400e', fontSize: 12.5,
    display: 'flex', alignItems: 'center', gap: 8,
  },

  // Mode toggle
  modeToggleRow: { display: 'flex', alignItems: 'center', gap: 16 },
  modeToggle: {
    display: 'flex', background: '#f0f0f5', borderRadius: 10, padding: 3,
    border: 'none', gap: 2,
  },
  modeTab: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'transparent', border: 'none', borderRadius: 8,
    padding: '8px 18px', fontSize: 13.5, fontWeight: 500, color: '#6e6e73', cursor: 'pointer',
  },
  modeTabActive: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#ffffff', border: 'none', borderRadius: 8,
    padding: '8px 18px', fontSize: 13.5, fontWeight: 600, color: '#1a3a5c', cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  },
  modeDesc: { fontSize: 12.5, color: '#6e6e73' },

  // Master-Ad "Aus Bild" upload
  masterAdPreviewWrap: {
    position: 'relative', borderRadius: 10, overflow: 'hidden',
    border: '1.5px solid #e8e8ed', background: '#fafafa',
    aspectRatio: '4/5', maxWidth: 180,
    display: 'flex', flexDirection: 'column',
  },
  masterAdPreviewImg: { width: '100%', height: '100%', objectFit: 'cover' as const, display: 'block' },
  masterAdOverlay: {
    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
    padding: 8,
    transition: 'background .15s',
  },
  masterAdReplaceBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'rgba(0,0,0,.6)', border: 'none', color: '#fff',
    borderRadius: 7, padding: '5px 10px', fontSize: 11.5, cursor: 'pointer',
  },
  masterAdFilename: {
    fontSize: 10.5, color: '#6e6e73', padding: '4px 8px',
    background: '#fafafa', borderTop: '1px solid #e8e8ed',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  },

  // Model dropdown metadata
  modelDesc: { fontSize: 11.5, color: '#6e6e73', marginTop: 2, fontStyle: 'italic' },
  apiSourceBadge: {
    fontSize: 10, fontWeight: 700, color: '#059669', background: '#d1fae5',
    borderRadius: 5, padding: '2px 7px',
  },
  defaultSourceBadge: {
    fontSize: 10, fontWeight: 700, color: '#6e6e73', background: '#f0f0f5',
    borderRadius: 5, padding: '2px 7px',
  },
};
